import { useCallback, useMemo, useRef, useState } from "react";
import { getEntityLockedTooltip, type Task } from "@common/types/task";
import type {
    BoardCardShortcut,
    BoardCardShortcutTarget,
    BoardPlacement,
} from "@design-system/Board/Board.types";
import { PLACEMENT_KEY_ATTRIBUTE } from "@design-system/Board/boardAttributes";
import { getPlacementKey } from "@design-system/Board/boardCells";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useTypedFlags } from "@hooks/useTypedFlags";
import type { TaskCardShortcut } from "./TaskBoardCard";
import { findTaskFieldChip } from "./TaskChipButton";
import { isTaskFieldEditable, type TaskCardEditField } from "./taskCardEdits";

const CONTROL_SHORTCUTS: ReadonlyArray<{
    key: string;
    label: string;
    field: TaskCardEditField;
}> = [
    { key: "a", label: "Assign", field: "assignee" },
    { key: "s", label: "Status", field: "status" },
    { key: "p", label: "Priority", field: "priority" },
    { key: "d", label: "Due date", field: "dueDate" },
    { key: "e", label: "Entities", field: "entities" },
];

/** A shortcut's control, open on the card copy it was pressed on. */
interface ShortcutRequest extends TaskCardShortcut {
    target: BoardCardShortcutTarget;
    placementKey: string | null;
}

interface UseTaskBoardShortcutsArgs {
    tasks: readonly Task[];
    getPlacements: (task: Task) => readonly BoardPlacement[];
    selectedItemIds: ReadonlySet<string>;
    /** What a shortcut does on a selected card instead of opening its control. */
    selectionActions: Partial<Record<TaskCardEditField, () => void>>;
}

/** A card shortcut that does nothing on a busy closes demo card, which can't be edited. */
const skipBusyClosesDemoCards =
    (onTrigger: (target: BoardCardShortcutTarget) => void) =>
    (target: BoardCardShortcutTarget) => {
        if (!isBusyClosesDemoId(target.itemId)) {
            onTrigger(target);
        }
    };

const getCardPlacementKey = (card: HTMLElement) =>
    card
        .closest(`[${PLACEMENT_KEY_ATTRIBUTE}]`)
        ?.getAttribute(PLACEMENT_KEY_ATTRIBUTE) ?? null;

/**
 * The board's card shortcuts for the focused card: A, S, P, D and E open the
 * assignee, status, priority, due date and entities controls over the card;
 * R renames, in place through the card's title with `taskTitleInlineEdit`
 * (a View-only user's plain title says why it can't), else in the title
 * modal. N and Shift+N are the board's own: a draft below or above the
 * card. On a selected card, a key with a selection
 * action runs that instead. E on an entity-locked task says why it
 * can't. Closing a control leaves focus to the Board's restore, which puts
 * it back on the card, or on its new card when the edit moved it.
 */
export const useTaskBoardShortcuts = ({
    tasks,
    getPlacements,
    selectedItemIds,
    selectionActions,
}: UseTaskBoardShortcutsArgs) => {
    const toaster = useToaster();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const { taskTitleInlineEdit: isRenameInline } = useTypedFlags();
    const [request, setRequest] = useState<ShortcutRequest | null>(null);
    const nextIdRef = useRef(0);
    const latestRef = useLatestRef({
        tasks,
        getPlacements,
        toaster,
        refuseIfViewOnly,
        selectedItemIds,
        selectionActions,
        isRenameInline,
    });

    if (request !== null) {
        const task = tasks.find(({ id }) => id === request.target.itemId);
        const isCardShown =
            task !== undefined &&
            getPlacements(task).some(
                (placement) =>
                    getPlacementKey(task.id, placement) ===
                    request.placementKey,
            );
        if (!isCardShown) {
            setRequest(null);
        }
    }

    const openRequest = useCallback(
        (field: TaskCardShortcut["field"], target: BoardCardShortcutTarget) => {
            nextIdRef.current += 1;
            setRequest({
                id: nextIdRef.current,
                field,
                target,
                placementKey: getCardPlacementKey(target.card),
            });
        },
        [],
    );

    const openControl = useCallback(
        (field: TaskCardEditField, target: BoardCardShortcutTarget) => {
            const latest = latestRef.current;
            const task = latest.tasks.find(({ id }) => id === target.itemId);
            if (task === undefined) {
                return;
            }
            if (!isTaskFieldEditable({ task, field })) {
                latest.toaster.info(getEntityLockedTooltip(task.type));
                return;
            }
            openRequest(field, target);
        },
        [latestRef, openRequest],
    );

    const rename = useCallback(
        (target: BoardCardShortcutTarget) => {
            const latest = latestRef.current;
            if (latest.isRenameInline) {
                const titleChip = findTaskFieldChip(target.card, "title");
                if (titleChip === null) {
                    latest.refuseIfViewOnly();
                } else {
                    titleChip.click();
                }
            } else {
                openRequest("title", target);
            }
        },
        [latestRef, openRequest],
    );

    const cardShortcuts = useMemo(
        (): readonly BoardCardShortcut[] => [
            ...CONTROL_SHORTCUTS.map(({ key, label, field }) => ({
                key,
                label,
                onTrigger: skipBusyClosesDemoCards(
                    (target: BoardCardShortcutTarget) => {
                        const latest = latestRef.current;
                        const selectionAction = latest.selectionActions[field];
                        if (
                            selectionAction !== undefined &&
                            latest.selectedItemIds.has(target.itemId)
                        ) {
                            selectionAction();
                            return;
                        }
                        openControl(field, target);
                    },
                ),
            })),
            {
                key: "r",
                label: "Rename",
                onTrigger: skipBusyClosesDemoCards(rename),
            },
        ],
        [latestRef, openControl, rename],
    );

    const endShortcut = useCallback(
        (shortcutId: number) =>
            setRequest((current) =>
                current?.id === shortcutId ? null : current,
            ),
        [],
    );

    return {
        cardShortcuts,
        request,
        endShortcut,
    };
};
